import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { apiConfig } from "../config/api.config";

const SheetSyncContext = createContext();

export const useSheetSync = () => {
  return useContext(SheetSyncContext);
};

export const SheetSyncProvider = ({ children }) => {
  const [sheetUrl, setSheetUrl] = useState(
    "https://docs.google.com/spreadsheets/d/141s8V_UACdBc8Xg17W0UhWxd08BMbEImkXOSPa66RfQ/edit?gid=474065919#gid=474065919",
  );
  const [syncStatus, setSyncStatus] = useState("idle"); // idle, connecting, syncing, error, success
  const [lastSync, setLastSync] = useState(null);
  const [syncInterval, setSyncInterval] = useState(300); // seconds (5 minutes default)
  const [autoSync, setAutoSync] = useState(false); // Disable auto sync by default to reduce API calls
  const [syncData, setSyncData] = useState([]);
  const [dataStatus, setDataStatus] = useState(null); // Status of all data sources
  const [error, setError] = useState(null);

  /**
   * Fetch unified leaderboard from all data sources.
   * Uses the new /data/leaderboard endpoint which merges:
   * - Primary spreadsheet (read-only legacy data)
   * - Writable spreadsheet (app-entered transition data)
   * - Database (games recorded in the app)
   */
  const performLiveSync = useCallback(async () => {
    try {
      setSyncStatus("connecting");
      setError(null);

      const API_URL = apiConfig.baseUrl;

      setSyncStatus("syncing");

      // Fetch unified leaderboard from all sources
      const response = await fetch(`${API_URL}/data/leaderboard?limit=100`);

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.statusText}`);
      }

      const leaderboardData = await response.json();
      setSyncData(leaderboardData);

      // Also fetch data sources status
      try {
        const statusResponse = await fetch(`${API_URL}/data/status`);
        if (statusResponse.ok) {
          const status = await statusResponse.json();
          setDataStatus(status);
          // Data sources status loaded
        }
      } catch (statusErr) {
        console.warn("Failed to fetch data status:", statusErr);
      }

      setLastSync(new Date());
      setSyncStatus("success");
      setTimeout(() => setSyncStatus("idle"), 2000);
    } catch (err) {
      console.error("Unified data sync error:", err);
      setError(err.message || "Unknown sync error occurred");
      setSyncStatus("error");
      setTimeout(() => setSyncStatus("idle"), 3000);
    }
  }, []);

  // Initial sync on load - fetch once when component mounts
  useEffect(() => {
    if (sheetUrl && syncData.length === 0 && syncStatus === "idle") {
      performLiveSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array = run once on mount

  // Auto sync interval
  useEffect(() => {
    let intervalId;
    if (autoSync && sheetUrl && syncInterval > 0) {
      intervalId = setInterval(() => {
        performLiveSync();
      }, syncInterval * 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoSync, sheetUrl, syncInterval, performLiveSync]);

  const value = {
    sheetUrl,
    setSheetUrl,
    syncStatus,
    lastSync,
    syncInterval,
    setSyncInterval,
    autoSync,
    setAutoSync,
    syncData,
    dataStatus, // Status of all data sources (primary sheet, writable sheet, database)
    error,
    performLiveSync,
  };

  return (
    <SheetSyncContext.Provider value={value}>
      {children}
    </SheetSyncContext.Provider>
  );
};
